import { FormattedMessage, useIntl } from 'react-intl';
import { NavLink, useLocation, useNavigate } from 'react-router';
import { DefaultUserMenu, EulaConsent } from '@rio-cloud/rio-user-menu-component';
import { RioNotifications } from '@rio-cloud/rio-notifications-component';
import ApplicationHeader from '@rio-cloud/rio-uikit/ApplicationHeader';
import ConfirmationDialog from '@rio-cloud/rio-uikit/ConfirmationDialog';
import IframeResizer from '@rio-cloud/iframe-resizer';
import { type MouseEvent, useCallback, useMemo, useState } from 'react';

import { config } from '../../config';
import { routes } from '../../routes/routes';
import { useAppDispatch, useAppSelector } from '../../configuration/setup/hooks';
import { accountType, getAccountTenant, getAccountType } from '../../configuration/login/loginSlice';
import { getIdToken } from '../../configuration/tokenHandling/tokenHandlingSlice';
import { InfoButton } from './InfoButton';
import { LogisticsMenu } from './LogisticsMenu';
import { OrderChat } from '@rio-cloud/rio-order-chat-component';
import { useGroupSidebarHasChanges, useUserSidebarHasChanges } from '../app/appSelectors';
import userSidebarSlice from '../app/users/usersidebar/userSidebarSlice';
import appSlice from '../app/appSlice';
import groupSidebarSlice from '../app/groups/groupsidebar/groupSidebarSlice';
import { getLinkTrackingAttributes } from '../../tracking/clickTracking';

type PendingDiscardTarget = 'user' | 'group' | null;

const AppHeader = () => {
    const environment = import.meta.env.PROD ? 'production' : 'local';

    const dispatch = useAppDispatch();
    const isCarrierAccount = useAppSelector(state => getAccountType(state) === accountType.CARRIER);
    const userSidebarHasChanges = useUserSidebarHasChanges();
    const groupSidebarHasChanges = useGroupSidebarHasChanges();
    // biome-ignore lint/style/noNonNullAssertion: suppress out of convenience for migration to biomejs
    const idToken = useAppSelector(getIdToken)!;

    const intl = useIntl();
    const location = useLocation();
    const navigate = useNavigate();
    const isLocalhost = import.meta.env.DEV;

    const noIframes = isIframesDisabled();
    const [pendingRoute, setPendingRoute] = useState<string | null>(null);
    const [pendingDiscardTarget, setPendingDiscardTarget] = useState<PendingDiscardTarget>(null);

    const activeDiscardTarget = useMemo<PendingDiscardTarget>(() => {
        if (location.pathname.startsWith(routes.GROUPS) && groupSidebarHasChanges) {
            return 'group';
        }

        if (location.pathname.startsWith(routes.DEFAULT) && userSidebarHasChanges) {
            return 'user';
        }

        return null;
    }, [groupSidebarHasChanges, location.pathname, userSidebarHasChanges]);

    const handleProtectedNavigation = useCallback(
        (event: MouseEvent<HTMLAnchorElement>, targetRoute: string) => {
            if (location.pathname === targetRoute || !activeDiscardTarget) {
                return;
            }

            event.preventDefault();
            setPendingRoute(targetRoute);
            setPendingDiscardTarget(activeDiscardTarget);
        },
        [activeDiscardTarget, location.pathname]
    );

    const clearPendingNavigation = useCallback(() => {
        setPendingRoute(null);
        setPendingDiscardTarget(null);
    }, []);

    const confirmPendingNavigation = useCallback(() => {
        if (!pendingRoute || !pendingDiscardTarget) {
            return;
        }

        if (pendingDiscardTarget === 'user') {
            dispatch(userSidebarSlice.actions.resetState());
            dispatch(appSlice.actions.userDeselected());
            dispatch(appSlice.actions.setNextUser(null));
        } else {
            dispatch(groupSidebarSlice.actions.resetGroupState());
            dispatch(appSlice.actions.groupDeselected());
            dispatch(appSlice.actions.setNextGroup(null));
        }

        clearPendingNavigation();
        navigate(pendingRoute);
    }, [clearPendingNavigation, dispatch, navigate, pendingDiscardTarget, pendingRoute]);

    // -----------------------------------
    // App navigator
    const url = config.backend.MENU_SERVICE;
    const appNavigator = isCarrierAccount ? (
        <IframeResizer title='Application navigation' className={'iFrameResizer'} src={url} />
    ) : (
        <LogisticsMenu />
    );

    // -----------------------------------
    // Navigation items
    const usersNavItem = {
        key: 'users',
        route: (
            <NavLink
                to={routes.DEFAULT}
                onClick={event => handleProtectedNavigation(event, routes.DEFAULT)}
                {...getLinkTrackingAttributes('users.subModuleName')}
            >
                <FormattedMessage id={'users.subModuleName'} />
            </NavLink>
        ),
    };

    const groupsNavItem = {
        key: 'groups',
        route: (
            <NavLink
                to={routes.GROUPS}
                onClick={event => handleProtectedNavigation(event, routes.GROUPS)}
                {...getLinkTrackingAttributes('groups.subModuleName')}
            >
                <FormattedMessage id={'groups.subModuleName'} />
            </NavLink>
        ),
    };

    const navItems = isCarrierAccount ? [usersNavItem, groupsNavItem] : [usersNavItem];

    // -----------------------------------
    // Action bar items
    const postLogout = config.postLogoutRedirectUri
        ? {
              redirectUri: config.postLogoutRedirectUri,
              idTokenHint: idToken,
          }
        : undefined;

    const tenant = useAppSelector(getAccountTenant) || 'unknown';
    const chat = <OrderChat tenant={tenant} />;
    const notifications = <RioNotifications />;
    const userMenu = <DefaultUserMenu environment={environment} postLogout={postLogout} />;
    const infoButton = <InfoButton appTitle={intl.formatMessage({ id: 'applicationmenu.useradmin.title' })} />;
    const carrierActionBarItems = [infoButton, chat, notifications, userMenu];

    const actionBarItems = noIframes ? [] : isCarrierAccount ? carrierActionBarItems : [userMenu];

    const homeRoute = isCarrierAccount ? (
        <a
            href={isLocalhost ? window.location.origin : 'https://home.rio.cloud'}
            {...getLinkTrackingAttributes('applicationHeader.home.carrier')}
        />
    ) : (
        <a
            href={isLocalhost ? window.location.origin : 'https://users.iam.rio.cloud'}
            {...getLinkTrackingAttributes('applicationHeader.home.default')}
        />
    );

    return (
        <>
            {pendingRoute && pendingDiscardTarget && (
                <ConfirmationDialog
                    bsSize={'sm'}
                    cancelButtonText={<FormattedMessage id={'dialog.discard.keepEditing'} />}
                    confirmButtonText={<FormattedMessage id={'dialog.discard.discardChanges'} />}
                    content={<FormattedMessage id={'dialog.discard.reallyDiscardChanges'} />}
                    onClickCancel={clearPendingNavigation}
                    onClickConfirm={confirmPendingNavigation}
                    show
                    title={<FormattedMessage id={'dialog.discard.discardChangesTitle'} />}
                />
            )}
            <ApplicationHeader
                label={intl.formatMessage({ id: 'applicationmenu.useradmin.title' })}
                appNavigator={appNavigator}
                homeRoute={homeRoute}
                navItems={navItems}
                actionBarItems={actionBarItems}
            />
            <EulaConsent environment={environment} />
        </>
    );
};

const isIframesDisabled = () => {
    try {
        return window.localStorage?.disableIframes?.toLowerCase() === 'true';
    } catch (_) {
        return false;
    }
};

export default AppHeader;
